import { useEffect, useMemo } from 'react';
import { useParams } from 'wouter';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useQuery } from '@tanstack/react-query';
import Header from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import type { FileObject } from '@shared/schema';
import { useAuthStore } from '@/store/auth';
import { useProjectsStore } from '@/store/projects';
import { ArrowLeft } from 'lucide-react';
import { authorizedFetch, scopedKey } from '@/lib/queryClient';

const PREVIEW_ROW_LIMIT = 200;

export default function ProjectDataPreviewPage() {
  const { projectId, fileId } = useParams<{ projectId?: string; fileId?: string }>();
  const { go } = useNav();
  const { user, isLoading: authLoading } = useAuthStore();
  const { currentProject, setCurrentProject } = useProjectsStore();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      go(routes.home());
      return;
    }
    if (!projectId) return;

    authorizedFetch(`/api/projects/${projectId}`)
      .then((res) => res.json())
      .then((project) => {
        if (!project?.id) throw new Error('Missing project');
        setCurrentProject(project);
      })
      .catch(() => go(routes.dashboard()));
  }, [authLoading, user, projectId, setCurrentProject, go]);

  const filesQuery = useQuery<FileObject[]>({
    queryKey: scopedKey([user?.id, '/api/projects', projectId, 'files']),
    enabled: !!projectId && !!user && !authLoading,
  });

  const file = useMemo(() => {
    if (!fileId) return null;
    return (filesQuery.data ?? []).find((item) => item.id === fileId) ?? null;
  }, [filesQuery.data, fileId]);
  const fileMeta = (file?.metadata as Record<string, any> | null | undefined) ?? {};
  const fileDisplayName = fileMeta.originalName ?? fileMeta.filename ?? file?.objectPath ?? 'Dataset';
  const rowCountDisplay = fileMeta.rowCount ?? '-';
  const columnCountDisplay = fileMeta.columnCount ?? '-';

  const previewQuery = useQuery<any[]>({
    queryKey: scopedKey([user?.id, '/api/files', fileId, 'data']),
    enabled: !!fileId && !!user && !authLoading,
  });

  const rows = useMemo(() => {
    if (!Array.isArray(previewQuery.data)) return [] as any[];
    return previewQuery.data.slice(0, PREVIEW_ROW_LIMIT);
  }, [previewQuery.data]);

  const handleBack = () => {
    go(routes.dashboard());
  };

  if (authLoading || filesQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-6 py-10">Loading dataset...</div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <div className="text-right text-sm text-muted-foreground">
            <div className="font-medium text-foreground">{currentProject?.name}</div>
            <div>{fileDisplayName}</div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Dataset Preview</h1>
            <p className="text-sm text-muted-foreground">
              Showing up to {PREVIEW_ROW_LIMIT} rows from {fileDisplayName}.
            </p>
          </div>
          <div className="text-sm text-muted-foreground text-right">
            Rows: {rowCountDisplay} · Columns: {columnCountDisplay}
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          {previewQuery.isLoading && (
            <div className="p-6 text-sm text-muted-foreground">Loading preview...</div>
          )}
          {previewQuery.isError && (
            <div className="p-6 text-sm text-destructive">
              {(previewQuery.error as Error)?.message || 'Failed to load preview.'}
            </div>
          )}
          {!previewQuery.isLoading && !previewQuery.isError && rows.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground">No preview available.</div>
          )}
          {!previewQuery.isLoading && !previewQuery.isError && rows.length > 0 && (
            <DataTable rows={rows} />
          )}
        </div>
      </div>
    </div>
  );
}

function DataTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) return null;
  const columns = Object.keys(rows[0] ?? {});

  return (
    <div className="max-h-[70vh] overflow-auto">
      <table className="w-full text-xs">
        <thead className="bg-muted sticky top-0 z-10">
          <tr className="border-b">
            {columns.map((col) => (
              <th key={col} className="px-3 py-2 text-left font-medium text-muted-foreground">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b odd:bg-muted/20">
              {columns.map((col) => (
                <td key={col} className="px-3 py-2 whitespace-nowrap">
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatCell(value: unknown) {
  if (value == null) return '';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
